import { useMutation } from '@tanstack/react-query'

import type {
  CreateAssetAssignmentParams,
  CreateAssetAssignmentResponse,
  CreateAssetGroupParams,
  CreateAssetGroupResponse,
  CreateAssetParams,
  CreateAssetResponse,
  CreateAssetTargetParams,
  CreateAssetTargetResponse,
  CreateAssetTrackerParams,
  CreateAssetTrackerResponse,
  UpdateAssetAssignmentParams,
  UpdateAssetAssignmentResponse,
  UpdateAssetGroupParams,
  UpdateAssetGroupResponse,
  UpdateAssetParams,
  UpdateAssetResponse,
  UpdateAssetTargetParams,
  UpdateAssetTargetResponse,
  UpdateAssetTrackerParams,
  UpdateAssetTrackerResponse,
} from '../services/api/api.types'
import type { MutationBaseOpts } from '../utils/tanstackHelpers'
import { handleMutationError, handleMutationSuccess } from '../utils/tanstackHelpers'
import { useGlobalState } from './useGlobalState'

export function useAssetUpdateMutation(opts?: MutationBaseOpts<UpdateAssetParams, UpdateAssetResponse>) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.updateAsset,
    onSuccess: handleMutationSuccess('Asset was updated successfully.', [['asset-sentry']], opts),
    onError: handleMutationError('Could Not Update Asset', opts),
  })
}

export function useAssetCreateMutation(opts?: MutationBaseOpts<CreateAssetParams, CreateAssetResponse>) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.createAsset,
    onSuccess: handleMutationSuccess(
      'Asset was created successfully.',
      [
        ['asset-sentry', 'assets'],
        ['asset-sentry', 'asset-trackers'],
        ['asset-sentry', 'asset-groups'],
      ],
      opts,
    ),
    onError: handleMutationError('Could Not Create Asset', opts),
  })
}

export function useAssetGroupCreateMutation(opts?: MutationBaseOpts<CreateAssetGroupParams, CreateAssetGroupResponse>) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.createAssetGroup,
    onSuccess: handleMutationSuccess('Group was created successfully.', [['asset-sentry', 'asset-groups']], opts),
    onError: handleMutationError('Could Not Create Group', opts),
  })
}

export function useAssetGroupUpdateMutation(opts?: MutationBaseOpts<UpdateAssetGroupParams, UpdateAssetGroupResponse>) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.updateAssetGroup,

    onSuccess: handleMutationSuccess('Group was updated successfully.', [['asset-sentry']], opts),
    onError: handleMutationError('Could Not Update Group', opts),
  })
}

export function useAssetTrackerCreateMutation(
  opts?: MutationBaseOpts<CreateAssetTrackerParams, CreateAssetTrackerResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.createAssetTracker,
    onSuccess: handleMutationSuccess('Tracker was created successfully.', [['asset-sentry', 'asset-trackers']], opts),
    onError: handleMutationError('Could Not Create Tracker', opts),
  })
}

export function useAssetTrackerUpdateMutation(
  opts?: MutationBaseOpts<UpdateAssetTrackerParams, UpdateAssetTrackerResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.updateAssetTracker,
    onSuccess: handleMutationSuccess(
      'Tracker was updated successfully.',
      [
        ['asset-sentry', 'asset-trackers'],
        ['asset-sentry', 'assets'],
      ],
      opts,
    ),
    onError: handleMutationError('Could Not Update Tracker', opts),
  })
}

export function useAssetTargetCreateMutation(
  opts?: MutationBaseOpts<CreateAssetTargetParams, CreateAssetTargetResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.createAssetTarget,
    onSuccess: handleMutationSuccess('Target was created successfully.', [['asset-sentry', 'asset-targets']], opts),
    onError: handleMutationError('Could Not Create Target', opts),
  })
}

export function useAssetTargetUpdateMutation(
  opts?: MutationBaseOpts<UpdateAssetTargetParams, UpdateAssetTargetResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.updateAssetTarget,
    onSuccess: handleMutationSuccess('Target was updated successfully.', [['asset-sentry']], opts),
    onError: handleMutationError('Could Not Update Target', opts),
  })
}

export function useAssetAssignmentCreateMutation(
  opts?: MutationBaseOpts<CreateAssetAssignmentParams, CreateAssetAssignmentResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.createAssetAssignment,
    onSuccess: handleMutationSuccess('Assignment was created successfully.', [['users'], ['asset-sentry']], opts),
    onError: handleMutationError('Could Not Create Assignment', opts),
  })
}

export function useAssetAssignmentUpdateMutation(
  opts?: MutationBaseOpts<UpdateAssetAssignmentParams, UpdateAssetAssignmentResponse>,
) {
  const { api } = useGlobalState()

  return useMutation({
    mutationFn: api.updateAssetAssignment,
    onSuccess: handleMutationSuccess('Assignment was updated successfully.', [['asset-sentry']], opts),
    onError: handleMutationError('Could Not Update Assignment', opts),
  })
}
